import { Component, Input } from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AbstractShape } from '../../../abstract/Shape';
import { SimulationValues } from '../../../clases/SimulationValues';
import { IField } from '../../../interfaces/Field';
import { ControlSliderComponent } from '../../control-slider/control-slider.component';

type SimulationStepDirection = 'previous' | 'next';

export interface SimulationStepRelations {
  previousAutoNodeId: string | null;
  nextAutoNodeId: string | null;
  previousAutoHidden: boolean;
  nextAutoHidden: boolean;
  previousManualNodeIds: string[];
  nextManualNodeIds: string[];
  selectedPreviousNodeId: string | null;
  selectedNextNodeId: string | null;
}

interface SimulationResourceOption {
  resource_id: string;
  resource_name: string;
}

@Component({
  selector: 'app-simulation-step-panel',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    ControlSliderComponent
  ],
  templateUrl: './simulation-step-panel.component.html',
  styleUrl: './simulation-step-panel.component.scss'
})
export class SimulationStepPanelComponent {
  @Input({ required: true }) step!: AbstractShape;
  @Input({ required: true }) stepOrder!: number | string | null;
  @Input({ required: true }) title!: string;
  @Input({ required: true }) description!: string;
  @Input({ required: true }) relation!: SimulationStepRelations;
  @Input({ required: true }) simulationSteps: AbstractShape[] = [];
  @Input({ required: true }) stepCodeByNodeId: Record<string, number> = {};
  @Input({ required: true }) simulationResourcesStructure: SimulationResourceOption[] = [];
  @Input({ required: true }) taskDurationVariabilityOptions: string[] = [];
  @Input({ required: true }) previusTaskDepencency: IField[] = [];
  @Input({ required: true }) nextTaskType: IField[] = [];
  @Input() showDragHandle = false;

  showAdvancedSettings = false;

  get stepId() {
    return this.step.dbPropierties.__EDT_UUID__;
  }

  get simulationValues() {
    return this.step.simulationValues;
  }

  setSimulationStepResource(simulationValues: SimulationValues, resourceId: string | null) {
    simulationValues.resource_id = resourceId;
    simulationValues.group = resourceId || '';
  }

  onNoWorkerChange(noWorker: boolean) {
    if (!this.simulationValues) {
      return;
    }

    this.simulationValues.no_worker = noWorker;
    if (!noWorker) {
      this.simulationValues.workersSlider.value = 1;
    }
  }

  onNextTask() {
    if (!this.simulationValues) {
      return;
    }

    this.simulationValues.nextTaskProb = this.simulationValues.nextTaskValue.name === this.nextTaskType[1]?.name;
  }

  getRelationNodeIds(direction: SimulationStepDirection) {
    const autoNodeId = direction === 'previous' ? this.relation.previousAutoNodeId : this.relation.nextAutoNodeId;
    const autoHidden = direction === 'previous' ? this.relation.previousAutoHidden : this.relation.nextAutoHidden;
    const manualNodeIds = direction === 'previous' ? this.relation.previousManualNodeIds : this.relation.nextManualNodeIds;

    return autoNodeId && !autoHidden ? [autoNodeId, ...manualNodeIds] : [...manualNodeIds];
  }

  getAvailableRelationOptions(direction: SimulationStepDirection) {
    const selectedNodeIds = new Set(this.getRelationNodeIds(direction));

    return this.simulationSteps.filter((node) => {
      return node.dbPropierties.__EDT_UUID__ !== this.stepId && !selectedNodeIds.has(node.dbPropierties.__EDT_UUID__);
    });
  }

  onRelationSelectionChange(direction: SimulationStepDirection, nodeId: string | null) {
    if (direction === 'previous') {
      this.relation.selectedPreviousNodeId = nodeId;
      return;
    }

    this.relation.selectedNextNodeId = nodeId;
  }

  addRelationNode(direction: SimulationStepDirection) {
    const selectedNodeId = direction === 'previous' ? this.relation.selectedPreviousNodeId : this.relation.selectedNextNodeId;
    if (!selectedNodeId || this.getRelationNodeIds(direction).includes(selectedNodeId)) {
      return;
    }

    if (direction === 'previous') {
      this.relation.previousManualNodeIds = [...this.relation.previousManualNodeIds, selectedNodeId];
      this.relation.selectedPreviousNodeId = null;
      this.syncStepRelationValues();
      return;
    }

    this.relation.nextManualNodeIds = [...this.relation.nextManualNodeIds, selectedNodeId];
    this.relation.selectedNextNodeId = null;
    this.syncStepRelationValues();
  }

  removeRelationNode(direction: SimulationStepDirection, nodeId: string) {
    if (direction === 'previous') {
      if (this.relation.previousAutoNodeId === nodeId && !this.relation.previousAutoHidden) {
        this.relation.previousAutoHidden = true;
        this.syncStepRelationValues();
        return;
      }

      this.relation.previousManualNodeIds = this.relation.previousManualNodeIds.filter((id) => id !== nodeId);
      this.syncStepRelationValues();
      return;
    }

    if (this.relation.nextAutoNodeId === nodeId && !this.relation.nextAutoHidden) {
      this.relation.nextAutoHidden = true;
      this.syncStepRelationValues();
      return;
    }

    this.relation.nextManualNodeIds = this.relation.nextManualNodeIds.filter((id) => id !== nodeId);
    this.syncStepRelationValues();
  }

  getSimulationStepCodeByNodeId(nodeId: string) {
    return this.stepCodeByNodeId[nodeId] ?? null;
  }

  getNodeNameByID(nodeId: string) {
    return this.simulationSteps.find((node) => {
      return node.dbPropierties.__EDT_UUID__ === nodeId;
    });
  }

  getTaskProbabilityValue(relationNodeId: string) {
    return this.getTaskProbabilityField(relationNodeId).value;
  }

  setTaskProbabilityValue(relationNodeId: string, value: string | number | null) {
    this.getTaskProbabilityField(relationNodeId).value = value == null ? '' : String(value);
  }

  private syncStepRelationValues() {
    if (!this.simulationValues) {
      return;
    }

    this.simulationValues.previous_task = this.getRelationTaskOrders('previous');
    this.simulationValues.next_task = this.getRelationTaskOrders('next');
    this.syncTaskProbabilityFields();
  }

  private getRelationTaskOrders(direction: SimulationStepDirection) {
    return this.getRelationNodeIds(direction)
      .map((nodeId) => this.getSimulationStepCodeByNodeId(nodeId))
      .filter((stepCode): stepCode is number => stepCode !== null);
  }

  private getTaskProbabilityField(relationNodeId: string): IField {
    const taskProbability = this.simulationValues!.taskProbability;
    const relationStepId = this.getRelationStepId(relationNodeId);
    let field = taskProbability.find((item) => item.name === relationStepId || item.name === relationNodeId);

    if (!field) {
      field = { name: relationStepId, value: '' };
      taskProbability.push(field);
    } else if (field.name !== relationStepId) {
      field.name = relationStepId;
    }

    return field;
  }

  private getRelationStepId(nodeId: string) {
    const stepCode = this.getSimulationStepCodeByNodeId(nodeId);
    return stepCode == null ? nodeId : String(stepCode);
  }

  private syncTaskProbabilityFields() {
    if (!this.simulationValues) {
      return;
    }

    const nextRelationIds = new Set(this.getRelationNodeIds('next'));
    this.simulationValues.taskProbability = this.simulationValues.taskProbability.filter((field) => {
      return this.simulationSteps.some((node) => {
        const nodeId = node.dbPropierties.__EDT_UUID__;
        return nextRelationIds.has(nodeId)
          && (field.name === this.getRelationStepId(nodeId) || field.name === nodeId);
      });
    });
  }

}
